import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoot } from 'react-dom/client';
import { v4 as uuidv4 } from 'uuid';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import L from 'leaflet';
import { GeoJsonObject } from 'geojson';
import { FeatureGroup, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { GeojsonProperties } from 'graphql-common';
import usePrevious from '@lib/hooks/usePrevious';
import { MapParams, PopupContentType } from './types';
import { NOMINATIM_BASE } from './constants';
import CenterButtonControl from './CenterButtonControl';
import FullScreenButtonControl from './FullScreenButtonControl';
import SearchAutocomplete from './SearchAutocomplete';
import OverlappingMarkerSpiderfier from './OverlappingMarkerSpiderfier';
import getMarker from './utils/markerUtils';
import calculatePopupOffset from './utils/calculatePopupOffset';
import calculatePolygonPopupOffset from './utils/calculatePolygonPopupOffset';
import { normalizeCoordinates } from './utils/normalizeCoordinates';
import './leaflet.scss';
import './leaflet.draw.scss';

function MapInner({
  disableAutoFit,
  featureGroupRef,
  geoJson,
  maxMarkers,
  maxPolygons,
  onCenterMap,
  onChange,
  pointerProps,
  polygonColor,
  polygonDashArray,
  polygonFillOpacity = 0.4,
  PopupContent,
  setMapParams,
  showCenterTool = true,
  showDrowTool = true,
}: {
  disableAutoFit?: boolean;
  featureGroupRef: React.RefObject<L.FeatureGroup | null>;
  geoJson: GeoJsonObject;
  maxMarkers: number;
  maxPolygons: number;
  onCenterMap?: () => void;
  onChange?: (value: GeoJsonObject) => void;
  pointerProps?: GeojsonProperties;
  polygonColor: string;
  polygonDashArray?: string | number[];
  polygonFillOpacity?: number;
  PopupContent?: PopupContentType;
  setMapParams?: (params: MapParams) => void;
  showCenterTool?: boolean;
  showDrowTool?: boolean;
}) {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const map = useMap();
  const prevPointerProps = usePrevious(pointerProps);
  const prevGeoJson = usePrevious(geoJson);
  const spiderfierRef = useRef<OverlappingMarkerSpiderfier | null>(null);
  const lastInternalChangeTimeRef = useRef<number>(0);
  const lastProcessedInternalChangeTimeRef = useRef<number>(0);
  const handleEditedInProgressRef = useRef(false);

  useEffect(() => {
    if (!map) return undefined;
    spiderfierRef.current = new OverlappingMarkerSpiderfier(map, {
      keepSpiderfied: true,
      nearbyDistance: 10,
      circleFootSeparation: 100,
    });
    return () => {
      spiderfierRef.current?.clearMarkers();
      spiderfierRef.current = null;
    };
  }, [map]);

  const syncSpiderfier = useCallback(() => {
    const spiderfier = spiderfierRef.current;
    const group = featureGroupRef.current;
    if (!spiderfier || !group) return;
    spiderfier.clearMarkers();
    group.eachLayer((l: any) => {
      if (l instanceof L.Marker) {
        spiderfier.addMarker(l);
      }
    });
  }, [featureGroupRef]);

  const hasDrowTool = !!onChange && showDrowTool;
  const markerIcon = useMemo(
    () => getMarker(pointerProps || ({} as GeojsonProperties), t),
    [pointerProps, t],
  );

  const logMap = useCallback(() => {
    if (!setMapParams || !map) return;
    try {
      const center = map.getCenter();
      const zoom = map.getZoom();
      const bounds = map.getBounds();
      const maxLat = bounds.getNorthEast().lat;
      const maxLon = bounds.getNorthEast().lng;
      const minLat = bounds.getSouthWest().lat;
      const minLon = bounds.getSouthWest().lng;
      const newMapParams = {
        maxLat,
        maxLon,
        minLat,
        minLon,
        zoom,
        center,
      };
      setMapParams(newMapParams);
    } catch (e) {
      console.error(e);
    }
  }, [map, setMapParams]);

  // Reverse geocode to get a human-readable address
  const fetchAddress = useCallback(
    async ({ lat, lon }: { lat: number; lon: number }): Promise<string> => {
      try {
        const url = `${NOMINATIM_BASE}/reverse?format=jsonv2&lat=${lat}&lon=${lon}`;
        const res = await fetch(url, {
          headers: {
            Accept: 'application/json',
            'Accept-Language': i18n.language,
          },
        });
        if (!res.ok) return '';
        const data = await res.json();
        return data?.display_name || '';
      } catch {
        return '';
      }
    },
    [i18n.language],
  );

  // Ensure layer has a feature/properties object
  const ensureLayerFeature = (layer: L.Layer): any => {
    const anyLayer: any = layer as any;
    if (!anyLayer.feature)
      anyLayer.feature = { type: 'Feature', properties: {} };
    if (!anyLayer.feature.properties) anyLayer.feature.properties = {};
    return anyLayer.feature.properties;
  };

  // Set address for a marker based on its current latlng
  const setAddressForMarker = useCallback(
    async (marker: L.Marker): Promise<void> => {
      const { lat, lng } = marker.getLatLng();
      const address = await fetchAddress({ lat, lon: lng });
      const props = ensureLayerFeature(marker);
      // Preserve existing properties and set address
      props.address = address;
    },
    [fetchAddress],
  );

  // Use refs to keep callbacks stable
  const pointerPropsRef = useRef(pointerProps);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    pointerPropsRef.current = pointerProps;
    onChangeRef.current = onChange;
  }, [pointerProps, onChange]);

  const handleChange = useCallback(() => {
    const group = featureGroupRef.current;
    if (!group) return;

    syncSpiderfier();

    const newGeoJson = group.toGeoJSON();
    if (onChangeRef.current && newGeoJson.type === 'FeatureCollection') {
      const updatedGeoJson = {
        ...newGeoJson,
        features: newGeoJson.features.map((feature) => ({
          ...feature,
          properties: {
            ...feature.properties,
            ...pointerPropsRef.current,
          },
        })),
      };
      // Mark that this change is coming from internal editing (set timestamp)
      lastInternalChangeTimeRef.current = Date.now();
      onChangeRef.current(updatedGeoJson);
    }
  }, [featureGroupRef, syncSpiderfier]);

  const handleChangeRef = useRef(handleChange);
  const setAddressForMarkerRef = useRef(setAddressForMarker);

  useEffect(() => {
    handleChangeRef.current = handleChange;
    setAddressForMarkerRef.current = setAddressForMarker;
  }, [handleChange, setAddressForMarker]);

  const addMarker = useCallback(
    ({ lat, lon }: { lat: number; lon: number }) => {
      const group = featureGroupRef.current;
      if (!group) return false;
      if (maxMarkers <= 0) return false;

      // Normalize coordinates to prevent invalid values when zoomed out
      const normalized = normalizeCoordinates({ lat, lon });

      const limit = maxMarkers > 0 ? maxMarkers : Infinity;
      if (limit === 1) {
        const toRemove: L.Marker[] = [];
        group.eachLayer((l: any) => {
          if (l instanceof L.Marker) toRemove.push(l);
        });
        toRemove.forEach((m) => group.removeLayer(m));
        spiderfierRef.current?.clearMarkers();
        const marker = L.marker(
          { lat: normalized.lat, lng: normalized.lon },
          { icon: markerIcon },
        );
        group.addLayer(marker);
        spiderfierRef.current?.addMarker(marker);
        setAddressForMarker(marker).then(() => handleChange());
        return true;
      }
      let count = 0;
      group.eachLayer((l: any) => {
        if (l instanceof L.Marker) count += 1;
      });
      if (count >= limit) return false;
      const marker = L.marker(
        { lat: normalized.lat, lng: normalized.lon },
        { icon: markerIcon },
      );
      group.addLayer(marker);
      spiderfierRef.current?.addMarker(marker);
      setAddressForMarker(marker).then(() => handleChange());
      return true;
    },
    [
      featureGroupRef,
      maxMarkers,
      markerIcon,
      handleChange,
      setAddressForMarker,
      spiderfierRef,
    ],
  );

  const handleCreated = (e: any) => {
    const group = featureGroupRef.current;
    if (!group) return;
    const limit = maxMarkers > 0 ? maxMarkers : Infinity;
    const polyLimit = maxPolygons > 0 ? maxPolygons : 0;
    const { layer } = e;
    if (layer instanceof L.Marker) {
      if (maxMarkers <= 0) {
        group.removeLayer(layer);
        handleChange();
        return;
      }

      // Normalize marker coordinates to prevent invalid values when zoomed out
      const latlng = layer.getLatLng();
      const normalized = normalizeCoordinates({
        lat: latlng.lat,
        lon: latlng.lng,
      });
      layer.setLatLng({ lat: normalized.lat, lng: normalized.lon });

      if (limit === 1) {
        group.eachLayer((l: any) => {
          if (l instanceof L.Marker && l !== layer) group.removeLayer(l);
        });
      } else {
        let count = 0;
        group.eachLayer((l: any) => {
          if (l instanceof L.Marker) count += 1;
        });
        if (count > limit) {
          group.removeLayer(layer);
          handleChange();
          return;
        }
      }
      // For a newly created marker, populate address then propagate change
      spiderfierRef.current?.addMarker(layer as L.Marker);
      setAddressForMarker(layer as L.Marker).then(() => handleChange());
      return;
    }
    if (layer instanceof L.Polygon) {
      if (polyLimit === 0) {
        group.removeLayer(layer);
        handleChange();
        return;
      }
      if (polyLimit === 1) {
        // Remove all existing polygons first, then keep the new one
        group.eachLayer((l: any) => {
          if (l instanceof L.Polygon && l !== layer) group.removeLayer(l);
        });
      } else {
        let polyCount = 0;
        group.eachLayer((l: any) => {
          if (l instanceof L.Polygon) polyCount += 1;
        });
        if (polyCount > polyLimit) {
          group.removeLayer(layer);
          handleChange();
          return;
        }
      }
    }
    handleChange();
  };

  const handleEdited = useCallback(
    (e: any) => {
      // Prevent duplicate execution if already in progress (due to accumulated event listeners)
      // Check and set flag SYNCHRONOUSLY before any other operations
      if (handleEditedInProgressRef.current) {
        return;
      }

      handleEditedInProgressRef.current = true;

      const editedMarkers: L.Marker[] = [];
      e.layers.eachLayer((l: any) => {
        if (l instanceof L.Marker) {
          // Normalize edited marker coordinates to prevent invalid values
          const latlng = l.getLatLng();
          const normalized = normalizeCoordinates({
            lat: latlng.lat,
            lon: latlng.lng,
          });
          l.setLatLng({ lat: normalized.lat, lng: normalized.lon });

          // Preserve custom marker style by reapplying the icon with current pointerProps from ref
          const updatedIcon = getMarker(
            pointerPropsRef.current || ({} as GeojsonProperties),
            t,
          );
          l.setIcon(updatedIcon);

          editedMarkers.push(l);
        }
      });

      if (editedMarkers.length) {
        Promise.all(editedMarkers.map((m) => setAddressForMarkerRef.current(m)))
          .then(() => {
            handleChangeRef.current();
            // Small delay before allowing next edit to prevent rapid re-triggers
            setTimeout(() => {
              handleEditedInProgressRef.current = false;
            }, 100);
          })
          .catch(() => {
            handleChangeRef.current();
            setTimeout(() => {
              handleEditedInProgressRef.current = false;
            }, 100);
          });
      } else {
        handleChangeRef.current();
        setTimeout(() => {
          handleEditedInProgressRef.current = false;
        }, 100);
      }
    },
    [t],
  );

  const updatePolygonsStyle = useCallback(
    (color: string, fillOpacity?: number, dashArray?: string | number[]) => {
      const group = featureGroupRef.current;
      if (!group) return;

      group.eachLayer((layer: any) => {
        // Only apply styles to polygons and polylines, not markers
        if (layer instanceof L.Polygon || layer instanceof L.Polyline) {
          layer.setStyle({
            fillColor: color,
            color,
            fillOpacity: fillOpacity || 0.4,
            dashArray,
          });
        }
      });
    },
    [featureGroupRef],
  );

  useEffect(() => {
    if (!(geoJson && Object.keys(geoJson).length) || !featureGroupRef.current)
      return;

    // Skip if this change came from our internal editing, but allow the first one through
    // This prevents re-mounting EditControl multiple times but allows layers to refresh once
    const timeSinceLastInternalChange =
      Date.now() - lastInternalChangeTimeRef.current;
    const isRecentInternalChange = timeSinceLastInternalChange < 5000;

    if (
      isRecentInternalChange &&
      lastProcessedInternalChangeTimeRef.current ===
        lastInternalChangeTimeRef.current
    ) {
      return;
    }

    if (isRecentInternalChange) {
      lastProcessedInternalChangeTimeRef.current =
        lastInternalChangeTimeRef.current;
    }

    // Only recreate layers if geoJson structure actually changed, not just pointerProps
    if (prevGeoJson && _isEqual(geoJson, prevGeoJson)) {
      return;
    }

    const group = featureGroupRef.current;

    // Clear existing layers before adding new ones
    group.clearLayers();
    spiderfierRef.current?.clearMarkers();

    const onEachFeature = (feature, layer) => {
      const { geometry, properties } = feature;
      const { type } = properties;
      const id = uuidv4();

      // Handle popups for both markers and polygons
      if (
        (layer instanceof L.Marker ||
          layer instanceof L.Polygon ||
          layer instanceof L.Polyline) &&
        geometry &&
        type !== 'CLUSTER' &&
        PopupContent !== undefined
      ) {
        const popupContainer = document.createElement('div');
        const root = createRoot(popupContainer);

        const renderPopupContent = (isPopupOpen = false) => {
          // For polygons, calculate center coordinates for popup positioning
          let coordinates = _get(geometry, 'coordinates');
          if (layer instanceof L.Polygon && geometry.type === 'Polygon') {
            // Get center of polygon bounds
            const bounds = layer.getBounds();
            const center = bounds.getCenter();
            coordinates = [center.lng, center.lat];
          } else if (
            layer instanceof L.Polyline &&
            geometry.type === 'LineString'
          ) {
            // For lines, use the middle point
            const coords = geometry.coordinates;
            const midIndex = Math.floor(coords.length / 2);
            coordinates = coords[midIndex];
          }

          root.render(
            <PopupContent
              key={id}
              properties={properties}
              coordinates={coordinates}
              isOpen={isPopupOpen}
            />,
          );
        };

        renderPopupContent(false);

        // Calculate popup offset for markers and polygons
        let popupOptions: L.PopupOptions = {
          closeButton: true,
          autoClose: false,
          closeOnClick: true,
          keepInView: false,
          autoPan: false,
          autoPanPadding: [60, 60],
          maxWidth: 265,
          minWidth: 265,
        };

        if (layer instanceof L.Marker) {
          const { offset: dynamicOffset, className } = calculatePopupOffset(
            layer as L.Marker,
            map,
          );
          popupOptions = {
            ...popupOptions,
            offset: dynamicOffset,
            className,
          };
        } else if (layer instanceof L.Polygon || layer instanceof L.Polyline) {
          const { offset: dynamicOffset, className } =
            calculatePolygonPopupOffset(layer as L.Polygon | L.Polyline, map);
          popupOptions = {
            ...popupOptions,
            offset: dynamicOffset,
            className,
          };
        }

        layer.bindPopup(popupContainer, popupOptions);

        layer.on('popupopen', () => {
          renderPopupContent(true);
        });

        layer.on('popupclose', () => {
          renderPopupContent(false);
        });
      }
    };

    L.geoJSON(geoJson as GeoJsonObject, {
      pointToLayer: (feature, latlng) => {
        const properties = (feature as any)?.properties || {};
        return L.marker(latlng, {
          icon: getMarker(
            {
              ...properties,
            },
            t,
          ),
        });
      },
      onEachFeature: PopupContent ? onEachFeature : undefined,
      style: (feature) => {
        // Apply styles only to polygons and polylines
        if (
          feature?.geometry?.type === 'Polygon' ||
          feature?.geometry?.type === 'MultiPolygon' ||
          feature?.geometry?.type === 'LineString' ||
          feature?.geometry?.type === 'MultiLineString'
        ) {
          const polygonColorValue = feature?.properties?.color || polygonColor;
          return {
            color: polygonColorValue,
            dashArray: polygonDashArray,
            fillColor: polygonColorValue,
            fillOpacity: polygonFillOpacity,
            opacity: 1,
            weight: 2,
          };
        }
        return {};
      },
    }).eachLayer((layer) => {
      group.addLayer(layer);
      if (layer instanceof L.Marker) {
        spiderfierRef.current?.addMarker(layer);
      }
    });
  }, [
    geoJson,
    prevGeoJson,
    featureGroupRef,
    map,
    polygonColor,
    polygonFillOpacity,
    pointerProps,
    PopupContent,
    spiderfierRef,
    polygonDashArray,
    t,
  ]);

  useEffect(() => {
    updatePolygonsStyle(polygonColor, polygonFillOpacity, polygonDashArray);
  }, [polygonColor, polygonFillOpacity, polygonDashArray, updatePolygonsStyle]);

  useEffect(() => {
    if (disableAutoFit) return;
    if (!featureGroupRef.current) return;

    const group = featureGroupRef.current;
    const bounds = group.getBounds();

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [20, 20], maxZoom: 15 });
    }
  }, [disableAutoFit, featureGroupRef, geoJson, map]);

  const updateExistingMarkersStyle = useCallback(() => {
    const group = featureGroupRef.current;
    if (!group) return;

    const newIcon = getMarker(pointerProps || ({} as GeojsonProperties), t);
    group.eachLayer((layer: any) => {
      if (layer instanceof L.Marker) {
        layer.setIcon(newIcon);
      }
    });
  }, [featureGroupRef, pointerProps, t]);

  useEffect(() => {
    if (
      prevPointerProps !== undefined &&
      !_isEqual(pointerProps, prevPointerProps)
    ) {
      // Only update marker styles in place, don't trigger handleChange
      // because that would cause a loop (handleChange calls parent onChange,
      // which might update pointerProps, which triggers this effect again)
      updateExistingMarkersStyle();
    }
  }, [pointerProps, prevPointerProps, updateExistingMarkersStyle]);

  useEffect(() => {
    logMap();
  }, [logMap]);

  useEffect(() => {
    map.on('moveend', logMap);

    return () => {
      map.off('moveend', logMap);
    };
  }, [map, logMap]);

  return (
    <>
      {maxMarkers > 0 && (
        <SearchAutocomplete
          map={map}
          featureGroupRef={featureGroupRef}
          onAddMarker={addMarker}
        />
      )}
      {showCenterTool && (
        <CenterButtonControl
          map={map}
          featureGroupRef={featureGroupRef}
          onCenterMap={onCenterMap}
        />
      )}
      <FeatureGroup ref={featureGroupRef}>
        <EditControl
          key="edit-control"
          position="topleft"
          onCreated={hasDrowTool ? handleCreated : undefined}
          onEdited={hasDrowTool ? handleEdited : undefined}
          onDeleted={hasDrowTool ? handleChange : undefined}
          edit={{
            featureGroup: featureGroupRef.current!,
            edit: hasDrowTool
              ? {
                  selectedPathOptions: {
                    color: polygonColor,
                    fillColor: polygonColor,
                    weight: 2,
                    opacity: 1,
                    fillOpacity: polygonFillOpacity,
                    dashArray: polygonDashArray,
                  },
                }
              : false,
            remove: hasDrowTool,
          }}
          draw={{
            polyline: false,
            rectangle: false,
            circle: false,
            circlemarker: false,
            marker:
              hasDrowTool && maxMarkers > 0 ? { icon: markerIcon } : false,
            polygon:
              hasDrowTool && maxPolygons > 0
                ? {
                    allowIntersection: false,
                    showArea: false,
                    metric: true,
                    shapeOptions: {
                      fillColor: polygonColor,
                      color: polygonColor,
                      weight: 2,
                      opacity: 1,
                      fillOpacity: polygonFillOpacity,
                      dashArray: polygonDashArray,
                    },
                  }
                : false,
          }}
        />
      </FeatureGroup>
      <FullScreenButtonControl map={map} />
    </>
  );
}

export default memo(MapInner);
